/**
 * Access Exceptions — one-person exceptions layered on top of templates.
 *
 * Unlike the old overrides, these ARE enforced by the resolver:
 * a deny always beats a grant, expiry dates are respected, and every
 * exception shows up in the Explainer's "why" trace.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  LEVELS, LevelBadge, makeApi, sectionCard, inputStyle, primaryBtn, dangerLink, errorBanner,
} from './AccessControlShared';

const chip = (text, color) => (
  <span key={text} style={{
    display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    backgroundColor: `${color}15`, color, border: `1px solid ${color}40`, margin: '1px 3px 1px 0',
  }}>{text}</span>
);

export const AccessExceptionsView = ({ api, initialData = null }) => {
  const [exceptions, setExceptions] = useState(initialData?.exceptions || []);
  const [meta, setMeta] = useState(initialData?.meta || null);
  const [users, setUsers] = useState(initialData?.users || []);
  const [projects, setProjects] = useState(initialData?.projects || []);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(initialData?.showForm ?? false);

  const emptyForm = {
    userId: '', projectId: '', moduleId: '', levelOverride: '',
    grantGranular: [], denyGranular: [], reason: '', expiresAt: '',
  };
  const [form, setForm] = useState(initialData?.form || emptyForm);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [exceptionsRes, metaRes, usersRes, projectsRes] = await Promise.all([
        api('GET', '/access-control/exceptions'),
        api('GET', '/access-control/meta'),
        api('GET', '/access-control/users'),
        api('GET', '/access-control/projects'),
      ]);
      setExceptions(exceptionsRes.data);
      setMeta(metaRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
    } catch (e) {
      console.error(e);
      setError('Failed to load exceptions. ' + (e.message || ''));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!initialData) load();
  }, [initialData, load]);

  const modules = meta?.modules || [];
  const selectedModule = modules.find(m => m.id === Number(form.moduleId));
  const granularForModule = useMemo(
    () => (meta?.granularPermissions || []).filter(g => g.module_key === selectedModule?.key),
    [meta, selectedModule]
  );

  const toggleFormGranular = (field, key) => {
    setForm(prev => {
      const has = prev[field].includes(key);
      return { ...prev, [field]: has ? prev[field].filter(k => k !== key) : [...prev[field], key] };
    });
  };

  const createException = async () => {
    try {
      setSaving(true);
      setError(null);
      await api('POST', '/access-control/exceptions', {
        userId: form.userId,
        projectId: form.projectId ? Number(form.projectId) : null,
        moduleId: Number(form.moduleId),
        levelOverride: form.levelOverride === '' ? null : Number(form.levelOverride),
        grantGranular: form.grantGranular,
        denyGranular: form.denyGranular,
        reason: form.reason || null,
        expiresAt: form.expiresAt || null,
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (e) {
      console.error(e);
      setError('Failed to create exception. ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (exception) => {
    try {
      setError(null);
      await api('PUT', `/access-control/exceptions/${exception.id}`, {
        isActive: !exception.is_active,
        expiresAt: exception.expires_at,
      });
      await load();
    } catch (e) {
      console.error(e);
      setError('Failed to update exception. ' + (e.message || ''));
    }
  };

  const removeException = async (exception) => {
    if (!window.confirm(`Delete this exception for ${exception.display_name || exception.email}?`)) return;
    try {
      setError(null);
      await api('DELETE', `/access-control/exceptions/${exception.id}`);
      await load();
    } catch (e) {
      console.error(e);
      setError('Failed to delete exception. ' + (e.message || ''));
    }
  };

  const formValid = form.userId && form.moduleId
    && (form.levelOverride !== '' || form.grantGranular.length > 0 || form.denyGranular.length > 0);

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#6c757d' }}>Loading exceptions…</div>;
  }

  return (
    <div>
      {errorBanner(error)}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12.5, color: '#6c757d', maxWidth: 640 }}>
          Exceptions change one person's access on top of their template — grant an extra, deny an extra,
          or force a module level. <strong>Deny always wins.</strong> Everything here is enforced and audited.
        </div>
        <button style={primaryBtn()} onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Close' : '+ New exception'}
        </button>
      </div>

      {showForm && (
        <div style={{ ...sectionCard, padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <select style={{ ...inputStyle, minWidth: 220 }} value={form.userId} onChange={e => setForm({ ...form, userId: e.target.value })}>
              <option value="">Who…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
            </select>
            <select style={{ ...inputStyle, minWidth: 200 }} value={form.projectId} onChange={e => setForm({ ...form, projectId: e.target.value })}>
              <option value="">Everywhere (all projects)</option>
              {projects.map(p => <option key={p.id} value={p.id}>Only on {p.job_number} — {p.job_name}</option>)}
            </select>
            <select
              style={{ ...inputStyle, minWidth: 200 }}
              value={form.moduleId}
              onChange={e => setForm({ ...form, moduleId: e.target.value, grantGranular: [], denyGranular: [] })}
            >
              <option value="">Which module…</option>
              {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select style={{ ...inputStyle, minWidth: 200 }} value={form.levelOverride} onChange={e => setForm({ ...form, levelOverride: e.target.value })}>
              <option value="">Keep template level</option>
              {LEVELS.map(l => <option key={l.value} value={l.value}>Force level: {l.label}</option>)}
            </select>
          </div>

          {selectedModule && granularForModule.length > 0 && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e7e34', marginBottom: 4 }}>Grant extras</div>
                {granularForModule.map(g => (
                  <label key={g.key} style={{ display: 'block', fontSize: 12.5, color: '#333', marginBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={form.grantGranular.includes(g.key)}
                      onChange={() => toggleFormGranular('grantGranular', g.key)}
                    /> {g.name}
                  </label>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#a71d2a', marginBottom: 4 }}>Deny extras (beats everything)</div>
                {granularForModule.map(g => (
                  <label key={g.key} style={{ display: 'block', fontSize: 12.5, color: '#333', marginBottom: 2 }}>
                    <input
                      type="checkbox"
                      checked={form.denyGranular.includes(g.key)}
                      onChange={() => toggleFormGranular('denyGranular', g.key)}
                    /> {g.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ ...inputStyle, flex: 1, minWidth: 240 }}
              placeholder="Reason (shows up in the audit log and the Explainer)"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
            />
            <label style={{ fontSize: 12.5, color: '#495057' }}>
              Expires:{' '}
              <input
                type="date"
                style={inputStyle}
                value={form.expiresAt}
                onChange={e => setForm({ ...form, expiresAt: e.target.value })}
              />
            </label>
            <button style={primaryBtn(!formValid || saving)} disabled={!formValid || saving} onClick={createException}>
              {saving ? 'Saving…' : 'Create exception'}
            </button>
          </div>
        </div>
      )}

      <div style={{ ...sectionCard, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e9ecef', color: '#495057' }}>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Who</th>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Where</th>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Module</th>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Effect</th>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Reason</th>
              <th style={{ textAlign: 'left', padding: '10px 14px' }}>Expires</th>
              <th style={{ padding: '10px 14px' }} />
            </tr>
          </thead>
          <tbody>
            {exceptions.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 28, textAlign: 'center', color: '#868e96' }}>
                No exceptions. That is a good default — prefer templates over exceptions.
              </td></tr>
            )}
            {exceptions.map((ex, idx) => (
              <tr key={ex.id} style={{
                backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc',
                borderBottom: '1px solid #f1f3f5',
                opacity: ex.is_active ? 1 : 0.45,
              }}>
                <td style={{ padding: '9px 14px', fontWeight: 600 }}>{ex.display_name || ex.email}</td>
                <td style={{ padding: '9px 14px' }}>
                  {ex.project_id ? `${ex.job_number} — ${ex.job_name}` : <em style={{ color: '#868e96' }}>Everywhere</em>}
                </td>
                <td style={{ padding: '9px 14px' }}>{ex.module_name}</td>
                <td style={{ padding: '9px 14px' }}>
                  {ex.level_override !== null && <span style={{ marginRight: 6 }}>level → <LevelBadge level={ex.level_override} small /></span>}
                  {(ex.grant_granular || []).map(g => chip(`+ ${g}`, '#1e7e34'))}
                  {(ex.deny_granular || []).map(g => chip(`− ${g}`, '#a71d2a'))}
                </td>
                <td style={{ padding: '9px 14px', color: '#6c757d', maxWidth: 220 }}>{ex.reason || '—'}</td>
                <td style={{ padding: '9px 14px', color: '#6c757d' }}>
                  {ex.expires_at ? new Date(ex.expires_at).toLocaleDateString() : 'never'}
                </td>
                <td style={{ padding: '9px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button style={{ ...dangerLink, color: '#0d6efd' }} onClick={() => toggleActive(ex)}>
                    {ex.is_active ? 'disable' : 'enable'}
                  </button>
                  <button style={dangerLink} onClick={() => removeException(ex)}>delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AccessExceptionsTab = () => {
  const { instance, accounts } = useMsal();
  const api = useMemo(() => makeApi(instance, accounts), [instance, accounts]);
  return <AccessExceptionsView api={api} />;
};

export default AccessExceptionsTab;
